/**
 * REF-6 — the Refund Reasons card's real read path (GET /sales/live/v2/refund-reasons).
 *
 * Unlike `refund-log`, this endpoint is pre-segmented at the source (`itemReasons` /
 * `feeReasons`) — no text-based `reasonGroupOf()` classification needed, so this mapping is
 * a straight field rename rather than an approximation layer.
 *
 * Paginated: one `page` / `size` pair is sent and applies to BOTH sections, but each section
 * returns its own `totalPages`, so the card's single pager runs to the larger of the two and
 * a section that has run out simply comes back empty for that page.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useBranch } from '../../../context/BranchContext';
import { useAuth } from '../../../context/AuthContext';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchRefundReasonsRequest } from '../../../redux/reports/clickHouse/clickHouseActions';
import type { OrderSource } from '../../../redux/reports/clickHouse/clickHouseApi';
import type { RefundReasonApiRow } from '../../../interface/clickHouseInterface';
import type { ReasonRow, RefundSalesSource } from './refundModel';
import { NO_REASON } from './refundReasons';

const findToken = (obj: any, depth = 0): string | null => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    for (const key of ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'idToken', 'id_token']) {
        if (obj[key] && typeof obj[key] === 'string' && obj[key].length > 10) return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (typeof val === 'string' && val.length > 50) return val;
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findToken(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const toOrderSource = (source: RefundSalesSource): OrderSource | undefined =>
    source === 'direct' ? 'direct' : source === 'thirdparty' ? 'thirdparty' : undefined;

const toRows = (rows: RefundReasonApiRow[], group: ReasonRow['group']): ReasonRow[] =>
    // The API sends an empty string until a reason is captured on the refund.
    (rows ?? []).map((r) => ({
        reason: String(r.reason ?? '').trim() || NO_REASON,
        group,
        count: Number(r.occurrenceCount) || 0,
        amount: Number(r.totalAmount) || 0,
    }));

/** Rows per page for each section — the endpoint's own default, so a typical outlet sees one page. */
export const REFUND_REASONS_PAGE_SIZE = 10;

export interface UseRefundReasonsPeriodArgs {
    startDate?: string;
    endDate?: string;
    /** Defaults to 'overall'. */
    source?: RefundSalesSource;
    /** Defaults to REFUND_REASONS_PAGE_SIZE. */
    pageSize?: number;
}

export interface UseRefundReasonsPeriodResult {
    /** Item section then fee section, each in the API's own order (totalAmount desc). */
    reasons: ReasonRow[];
    /** True only while there is nothing to show yet (first load / a new period). */
    loading: boolean;
    /** True while ANY request is in flight, including a page change that keeps rows on screen. */
    fetching: boolean;
    failure: boolean;
    retry: () => void;
    /** 1-indexed current page. */
    page: number;
    pageSize: number;
    /** The larger of the two sections' totalPages — what the card's single pager runs to. */
    totalPages: number;
    setPage: (page: number) => void;
}

export function useRefundReasonsPeriod({ startDate, endDate, source = 'overall', pageSize = REFUND_REASONS_PAGE_SIZE }: UseRefundReasonsPeriodArgs): UseRefundReasonsPeriodResult {
    const dispatch = useAppDispatch();
    const { selectedBranch: branch } = useBranch();
    const { auth } = useAuth();
    const locationId = branch?.id ?? branch?.locationId ?? '';
    const token = useMemo(() => (auth ? findToken(auth) : null), [auth]);

    const orderSource = toOrderSource(source);

    // The page belongs to a context (outlet / period / source / size). When the context changes the
    // stored page no longer applies, so it reads back as page 1 — derived rather than reset in an
    // effect, so the new context is fetched ONCE, at page 1, instead of first at the stale page.
    const contextKey = `${locationId}|${startDate ?? ''}|${endDate ?? ''}|${orderSource ?? ''}|${pageSize}`;
    const [pageState, setPageState] = useState({ key: contextKey, page: 1 });
    const page = pageState.key === contextKey ? pageState.page : 1;
    const setPage = (next: number) => setPageState({ key: contextKey, page: Math.max(1, next) });

    // A page change keeps the rows up while it loads; a context change (incl. the first fetch on mount, so rows
    // left in the store by an earlier visit never label this one) clears them.
    const lastContextRef = useRef<string | null>(null);

    const fetch = (keepPrevious = false) => {
        // Same guard as useRefundLogPeriod — locationId and token resolve independently on
        // mount, and dispatching before both are ready double-hits the API.
        if (!locationId || !token) return;
        dispatch(fetchRefundReasonsRequest(locationId, startDate, endDate, orderSource, page, pageSize, keepPrevious));
    };

    useEffect(() => {
        const sameContext = lastContextRef.current === contextKey;
        lastContextRef.current = contextKey;
        fetch(sameContext);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, locationId, startDate, endDate, orderSource, token, page, pageSize]);

    const raw = useAppSelector((s) => s.clickHouse.refundReasonsSuccess);
    const fetching = useAppSelector((s) => s.clickHouse.refundReasonsLoading);
    const failure = useAppSelector((s) => s.clickHouse.refundReasonsFailure);

    // Order is the API's (amount desc within each section) — not re-sorted here, so a row never
    // jumps between pages.
    const reasons = useMemo(
        () => [...toRows(raw?.itemReasons?.content ?? [], 'item'), ...toRows(raw?.feeReasons?.content ?? [], 'fee')],
        [raw],
    );
    const totalPages = Math.max(1, raw?.itemReasons?.totalPages ?? 1, raw?.feeReasons?.totalPages ?? 1);

    return { reasons, loading: fetching && !raw, fetching, failure, retry: () => fetch(false), page, pageSize, totalPages, setPage };
}
